import React from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { TableCell, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '../ui/Checkbox'
import { Bot } from 'lucide-react'

interface DummyLLMAssignmentSectionProps {
  dummyBots: Array<{ id: number; name: string }>
  teams: Array<{ id: number; name: string }>
  isDummyAssignedToTeam: (dummyBotId: number, teamId: number) => boolean
  onDummyToggle: (teamId: number, dummyBotId: number) => void
  isMobile?: boolean
  isReadOnly?: boolean
}

export const DummyLLMAssignmentSection: React.FC<DummyLLMAssignmentSectionProps> =
  React.memo(
    ({
      dummyBots,
      teams,
      isDummyAssignedToTeam,
      onDummyToggle,
      isMobile = false,
      isReadOnly = false,
    }) => {
      if (isMobile) {
        return (
          <div className='space-y-4'>
            <div className='flex items-center gap-2'>
              <span className='text-purple-600 text-lg'>
                <Bot />
              </span>
              <h3 className='text-lg font-semibold text-purple-700'>AI Players</h3>
            </div>
            {dummyBots.length > 0 ? (
              <div className='space-y-4'>
                {dummyBots.map((bot) => (
                  <Card key={bot.id}>
                    <CardHeader className='pb-3'>
                      <h4 className='font-semibold text-gray-800'>{bot.name}</h4>
                    </CardHeader>
                    <CardContent>
                      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3'>
                        {teams.map((team) => (
                          <div
                            key={team.id}
                            className='flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100'
                          >
                            <span className='font-medium text-sm text-gray-700 truncate mr-2'>
                              {team.name}
                            </span>
                            <Checkbox
                              checked={isDummyAssignedToTeam(bot.id, team.id)}
                              onChange={() => onDummyToggle(team.id, bot.id)}
                              disabled={isReadOnly}
                            />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Alert className='border-dashed border-purple-200 bg-purple-50'>
                <AlertDescription className='text-sm text-purple-700'>
                  No AI Player configured. Create one from AI Player Settings.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )
      }

      return (
        <>
          <TableRow className='bg-purple-50 border-t-2 border-purple-200'>
            <TableCell className='p-4 font-semibold text-dark-blue border-r sticky left-0 z-10 bg-purple-50'>
              <div className='flex items-center gap-2'>
                <span className='text-purple-600 text-lg'>
                  <Bot />
                </span>
                <span>AI Players</span>
              </div>
            </TableCell>
            {teams.map((team) => (
              <TableCell key={team.id} className='p-4 bg-purple-50' />
            ))}
          </TableRow>
          {dummyBots.length > 0 ? (
            dummyBots.map((bot) => (
              <TableRow key={bot.id}>
                <TableCell className='p-4 border-r sticky left-0 z-10 bg-white'>
                  <div className='font-medium text-gray-800 truncate'>{bot.name}</div>
                </TableCell>
                {teams.map((team) => (
                  <TableCell key={team.id} className='p-4 text-center align-middle'>
                    <div className='flex justify-center items-center'>
                      <Checkbox
                        checked={isDummyAssignedToTeam(bot.id, team.id)}
                        onChange={() => onDummyToggle(team.id, bot.id)}
                        disabled={isReadOnly}
                      />
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                className='p-6 border-r sticky left-0 z-10 bg-white'
                colSpan={teams.length + 1}
              >
                <div className='text-sm text-gray-500 text-center'>
                  No AI Player configured. Create one from AI Player Settings.
                </div>
              </TableCell>
            </TableRow>
          )}
        </>
      )
    }
  )

DummyLLMAssignmentSection.displayName = 'DummyLLMAssignmentSection'
